/**
 * The assertions shared by the unit tests and the conformance runner.
 *
 * Each one is a claim about a finished stream, checked against the state the
 * reducer built and the timeline it kept. The runner applies them after every
 * scenario, so the same functions that pin the reducer in a unit test pin it
 * against the real server, over a real socket, in CI.
 *
 * Every failure throws `InvariantViolation` with a message a reader can act
 * on: which claim broke, where, and by how much.
 */

import { isTerminalPhase, type StreamState, type TraceEntry } from './reducer.ts';

export class InvariantViolation extends Error {
  readonly invariant: string;

  constructor(invariant: string, message: string) {
    super(`${invariant}: ${message}`);
    this.name = 'InvariantViolation';
    this.invariant = invariant;
  }
}

/**
 * The visible text is byte identical to the canonical text for the scenario.
 * This is the central invariant: it is what "resume without loss" means.
 */
export function assertNoLostTokens(state: StreamState, expectedCanonicalText: string): void {
  if (state.text === expectedCanonicalText) return;

  const limit = Math.min(state.text.length, expectedCanonicalText.length);
  let divergence = limit;
  for (let i = 0; i < limit; i += 1) {
    if (state.text[i] !== expectedCanonicalText[i]) {
      divergence = i;
      break;
    }
  }
  throw new InvariantViolation(
    'assertNoLostTokens',
    `text diverges from the canonical text at character ${String(divergence)}. ` +
      `Expected ${String(expectedCanonicalText.length)} characters, ` +
      `received ${String(state.text.length)}.`,
  );
}

/** No applied frame id appears twice in the timeline. Dedupe held. */
export function assertNoDuplicatedTokens(state: StreamState): void {
  const seen = new Set<number>();
  for (const entry of state.timeline) {
    if (entry.kind !== 'frame' || entry.id === null) continue;
    if (seen.has(entry.id)) {
      throw new InvariantViolation(
        'assertNoDuplicatedTokens',
        `frame id ${String(entry.id)} was applied twice.`,
      );
    }
    seen.add(entry.id);
  }
}

/** Applied frame ids only ever increase. Stale and duplicate discards held. */
export function assertMonotonicIds(timeline: readonly TraceEntry[]): void {
  let previous = 0;
  for (const entry of timeline) {
    if (entry.kind !== 'frame' || entry.id === null) continue;
    if (entry.id <= previous) {
      throw new InvariantViolation(
        'assertMonotonicIds',
        `frame id ${String(entry.id)} was applied after id ${String(previous)}.`,
      );
    }
    previous = entry.id;
  }
}

/** The stream ended in done, error, or cancelled rather than hanging. */
export function assertTerminalReached(state: StreamState): void {
  if (isTerminalPhase(state.phase)) return;
  throw new InvariantViolation(
    'assertTerminalReached',
    `the stream finished in phase "${state.phase}", which is not terminal.`,
  );
}

/**
 * Every skip in the applied id sequence is covered by a gap marker recorded
 * before the frame that revealed it, and hasGap agrees with the timeline.
 * A gap the client knows about is a fault survived; a gap it does not is
 * output silently presented as continuous.
 */
export function assertNoGapsUnlessMarked(state: StreamState): void {
  const marked = new Set<string>();
  let sawGapMarker = false;
  // The id sequence walks everything that consumed an id: applied frames and
  // dropped unknown events both advance the cursor.
  let previous = 0;

  for (const entry of state.timeline) {
    if (entry.kind === 'gap') {
      marked.add(`${String(entry.fromId)}>${String(entry.toId)}`);
      sawGapMarker = true;
      continue;
    }
    const id = entry.kind === 'frame' || entry.kind === 'unknown_event' ? entry.id : null;
    if (id === null || id <= previous) continue;
    if (id > previous + 1 && !marked.has(`${String(previous)}>${String(id)}`)) {
      throw new InvariantViolation(
        'assertNoGapsUnlessMarked',
        `ids ${String(previous + 1)} to ${String(id - 1)} were skipped with no gap marker.`,
      );
    }
    previous = id;
  }

  if (sawGapMarker && !state.hasGap) {
    throw new InvariantViolation(
      'assertNoGapsUnlessMarked',
      'the timeline records a gap but hasGap is false.',
    );
  }
  if (!sawGapMarker && state.hasGap) {
    throw new InvariantViolation(
      'assertNoGapsUnlessMarked',
      'hasGap is set but the timeline records no gap.',
    );
  }
}
